import { Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { CoreService } from '../../services'
import { SchemaUtil } from '../../utils'
import { companyCarriersColumns } from '../../utils/definitions.util'
import { CarrierService } from '../../services/entity-services/carrier.service'
import { injectInfiniteQuery, injectQuery } from '@tanstack/angular-query-experimental'

const DEFAULT_FILTERS = {
  carrier: '',
}

@Component({
  selector: 'app-company-carriers',
  templateUrl: 'company-carriers.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CompanyCarriersComponent extends BaseListing implements OnInit {
  private carrierService = inject(CarrierService)

  listingColumns = signal(companyCarriersColumns)

  sortProp = signal('id')
  sortDir = signal('asc')

  filters = signal(structuredClone(DEFAULT_FILTERS))

  // Stabilize filters in query key
  filtersKey = computed(() => JSON.stringify(this.queryFilters()))

  canCreate = computed(() => {
    return (
      this.core.services.user.hasRole('ROLE_ORDER_CREATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    )
  })

  readonly queryFilters = computed(() => {
    const value = this.filters()
    const f: Record<string, any> = {}
    if (value.carrier?.length) f['carrier'] = value.carrier
    return f
  })

  readonly queryCarriers = injectQuery(() => ({
    queryKey: ['carriers'],
    queryFn: async () => {
      return await this.carrierService
        .getCarriers(
          {
            sort: 'name',
            direction: 'asc',
          },
          { id: null, name: null }
        )
        .then(result => result.items)
    },
  }))

  readonly queryCompanyCarriers = injectInfiniteQuery(() => ({
    // Use only primitives/stringified filters in the key to keep it stable
    // queryKey: ['companyCarriers', this.sortProp(), this.sortDir(), this.filtersKey()],
    // queryKey: ['companyCarriers', this.sortProp(), this.sortDir(), this.filters()],
    queryKey: ['companyCarriers', this.sortProp(), this.sortDir()],
    queryFn: async ({ pageParam }) => {
      const r = await this.carrierService
        .getCompanyCarriers(
          {
            page: pageParam,
            sort: this.sortProp(),
            direction: this.sortDir(),
            filters: this.queryFilters(),
          },
          {
            id: null,
            enable: null,
            carrier: SchemaUtil.carrier,
            checkout: { id: null, name: null },
            externalId: null,
          }
        )
        .then(result => {
          this.saveViewSettings('companyCarriers', this.filters())
          return result
        })
      return r
    },
    initialPageParam: 1,
    getNextPageParam: result =>
      Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined,
  }))

  companyCarriers = computed(() => this.queryCompanyCarriers.data()?.pages?.flatMap(p => p.items) ?? [])
  totalItems = computed(() => this.queryCompanyCarriers.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  constructor(protected core: CoreService) {
    super(core)
    effect(() => {
      this.queryCarriers.data()
      this.queryCompanyCarriers.data()
    })
  }

  async ngOnInit() {
    this.filters.set(this.loadViewSettings('companyCarriers') || structuredClone(DEFAULT_FILTERS))
  }

  clearFilters(): void {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.queryCompanyCarriers.refetch()
  }

  updateFilterCarrier(carrierId: string): void {
    this.filters.update(v => ({ ...v, carrier: carrierId }))
    this.queryCompanyCarriers.refetch()
  }
}
